import { useParams } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { externalSupabase } from "@/lib/externalSupabase";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2, MapPin, BedDouble, Home, BadgeCheck, Calendar as CalendarIcon,
  Send, MessageSquare, Users, ChevronLeft, ChevronRight, ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  format, addMonths, startOfMonth, endOfMonth, eachDayOfInterval,
  isSameMonth, isSameDay, isWithinInterval, isBefore, startOfDay, getDay
} from "date-fns";

interface PropertyData {
  id: string;
  masked_title: string | null;
  original_name: string | null;
  image_url: string | null;
  city: string | null;
  country: string | null;
  bedrooms: number | null;
  property_type: string | null;
  description: string | null;
  is_verified: boolean | null;
  airbnb_url: string;
  gallery_urls: string[] | null;
  latitude: number | null;
  longitude: number | null;
  street_address: string | null;
  owner_website: string | null;
}

interface AvailabilityBlock {
  start_date: string;
  end_date: string;
  summary: string | null;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DirectBookingPage = () => {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();

  const [property, setProperty] = useState<PropertyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [blockedDates, setBlockedDates] = useState<AvailabilityBlock[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Gallery state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Booking form
  const [bookingForm, setBookingForm] = useState({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", guests_count: 1, message: ""
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);

  // Inquiry form
  const [inquiryForm, setInquiryForm] = useState({
    guest_name: "", guest_email: "", subject: "", message: ""
  });
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!id) { setLoading(false); return; }

      const [propRes, availRes] = await Promise.all([
        externalSupabase.from("properties").select("*").eq("id", id).maybeSingle(),
        supabase.from("availability_blocks").select("start_date, end_date, summary").eq("property_id", id)
      ]);

      if (propRes.data) setProperty(propRes.data as PropertyData);
      if (availRes.data) setBlockedDates(availRes.data);
      setLoading(false);
    };
    load();
  }, [id]);

  const allImages = useMemo(() => {
    if (!property) return [];
    const imgs: string[] = [];
    if (property.image_url) imgs.push(property.image_url);
    if (property.gallery_urls) imgs.push(...property.gallery_urls.filter(u => u && !imgs.includes(u)));
    return imgs;
  }, [property]);

  const isDateBlocked = (date: Date) => {
    return blockedDates.some(b =>
      isWithinInterval(date, {
        start: new Date(b.start_date + "T00:00:00"),
        end: new Date(b.end_date + "T00:00:00")
      })
    );
  };

  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property || !bookingForm.guest_name || !bookingForm.guest_email || !bookingForm.check_in || !bookingForm.check_out) {
      toast({ title: "Missing fields", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    setBookingSubmitting(true);
    try {
      const { error } = await supabase.from("booking_requests").insert({
        property_id: property.id,
        guest_name: bookingForm.guest_name,
        guest_email: bookingForm.guest_email,
        guest_phone: bookingForm.guest_phone || null,
        check_in: bookingForm.check_in,
        check_out: bookingForm.check_out,
        guests_count: bookingForm.guests_count,
        message: bookingForm.message || null,
      });
      if (error) throw error;
      setBookingSuccess(true);
      toast({ title: "Request sent!", description: "The host will review your booking and get back to you." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setBookingSubmitting(false);
    }
  };

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property || !inquiryForm.guest_name || !inquiryForm.guest_email || !inquiryForm.message) {
      toast({ title: "Missing fields", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    setInquirySubmitting(true);
    try {
      const { error } = await supabase.from("guest_inquiries").insert({
        property_id: property.id,
        guest_name: inquiryForm.guest_name,
        guest_email: inquiryForm.guest_email,
        subject: inquiryForm.subject || null,
        message: inquiryForm.message,
      });
      if (error) throw error;
      setInquirySuccess(true);
      toast({ title: "Message sent!", description: "The host will respond to your inquiry soon." });
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setInquirySubmitting(false);
    }
  };

  const displayTitle = property?.masked_title || property?.original_name || "Beautiful Vacation Rental";
  const locationText = [property?.city, property?.country].filter(Boolean).join(", ");
  const icalExportUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ical-sync?property_id=${id}&action=export`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Home className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Property Not Found</h1>
          <p className="text-muted-foreground">This listing doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Gallery */}
      <div className="relative">
        {allImages.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-4 md:grid-rows-2 gap-1 max-h-[500px] overflow-hidden">
            {/* Main image */}
            <div
              className="md:col-span-2 md:row-span-2 relative cursor-pointer group"
              onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
            >
              <img src={allImages[0]} alt={displayTitle} className="w-full h-full object-cover min-h-[300px] md:min-h-[500px]" />
              <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
            </div>
            {/* Secondary images */}
            {allImages.slice(1, 5).map((url, i) => (
              <div
                key={i}
                className="hidden md:block relative cursor-pointer group"
                onClick={() => { setLightboxIndex(i + 1); setLightboxOpen(true); }}
              >
                <img src={url} alt={`Photo ${i + 2}`} className="w-full h-full object-cover min-h-[248px]" />
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-colors" />
              </div>
            ))}
            {/* Photo count badge */}
            {allImages.length > 5 && (
              <button
                onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                className="absolute bottom-4 right-4 bg-card/90 backdrop-blur-sm border border-border px-3 py-1.5 rounded-lg text-sm font-medium text-foreground hover:bg-card transition-colors"
              >
                Show all {allImages.length} photos
              </button>
            )}
          </div>
        ) : (
          <div className="h-[300px] bg-muted flex items-center justify-center">
            <Home className="w-16 h-16 text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-6xl py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Property Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Title & Meta */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-start gap-3 mb-3">
                {property.is_verified && (
                  <Badge className="bg-primary text-primary-foreground gap-1">
                    <BadgeCheck className="w-3 h-3" /> Verified
                  </Badge>
                )}
                {property.property_type && (
                  <Badge variant="secondary">{property.property_type}</Badge>
                )}
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-3">
                {displayTitle}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                {locationText && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4 text-primary" /> {locationText}
                  </span>
                )}
                {property.bedrooms != null && (
                  <span className="flex items-center gap-1.5">
                    <BedDouble className="w-4 h-4 text-primary" /> {property.bedrooms} {property.bedrooms === 1 ? "Bedroom" : "Bedrooms"}
                  </span>
                )}
              </div>
            </motion.div>

            <div className="border-t border-border" />

            {/* Description */}
            <div>
              <h2 className="font-display text-xl font-semibold mb-3">About this property</h2>
              {property.description ? (
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{property.description}</p>
              ) : (
                <p className="text-muted-foreground/60 italic">
                  A wonderful vacation rental perfect for your next getaway. Contact the host for more details about amenities and the surrounding area.
                </p>
              )}
            </div>

            <div className="border-t border-border" />

            {/* Availability Calendar */}
            <div>
              <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" /> Availability
              </h2>
              <Card>
                <CardContent className="p-4">
                  {/* Calendar nav */}
                  <div className="flex items-center justify-between mb-4">
                    <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="font-display font-semibold text-foreground">
                      {format(currentMonth, "MMMM yyyy")}
                    </h3>
                    <Button variant="ghost" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {WEEKDAYS.map(d => (
                      <div key={d} className="text-center text-xs font-medium text-muted-foreground py-1">{d}</div>
                    ))}
                  </div>

                  {/* Days */}
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for offset */}
                    {Array.from({ length: getDay(calendarDays[0]) }).map((_, i) => (
                      <div key={`empty-${i}`} />
                    ))}
                    {calendarDays.map(day => {
                      const blocked = isDateBlocked(day);
                      const past = isBefore(day, startOfDay(new Date()));
                      const today = isSameDay(day, new Date());
                      return (
                        <div
                          key={day.toISOString()}
                          className={`
                            text-center py-2 text-sm rounded-md transition-colors
                            ${blocked ? "bg-destructive/15 text-destructive line-through" : ""}
                            ${past && !blocked ? "text-muted-foreground/40" : ""}
                            ${!blocked && !past ? "text-foreground hover:bg-accent" : ""}
                            ${today ? "ring-1 ring-primary font-semibold" : ""}
                          `}
                        >
                          {format(day, "d")}
                        </div>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-destructive/15 border border-destructive/30" /> Unavailable
                    </span>
                    <span className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded-sm bg-accent border border-border" /> Available
                    </span>
                  </div>

                  {/* iCal export */}
                  <div className="mt-4 pt-3 border-t border-border">
                    <a
                      href={icalExportUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary hover:underline flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Export calendar (iCal)
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="border-t border-border" />

            {/* Inquiry Form */}
            <div>
              <h2 className="font-display text-xl font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" /> Ask the Host
              </h2>
              {inquirySuccess ? (
                <Card>
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                      <Send className="w-5 h-5 text-primary" />
                    </div>
                    <h3 className="font-display font-semibold text-foreground mb-1">Message Sent!</h3>
                    <p className="text-sm text-muted-foreground">The host will get back to you soon.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleInquirySubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="inq-name">Name *</Label>
                          <Input id="inq-name" value={inquiryForm.guest_name} onChange={e => setInquiryForm(f => ({ ...f, guest_name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="inq-email">Email *</Label>
                          <Input id="inq-email" type="email" value={inquiryForm.guest_email} onChange={e => setInquiryForm(f => ({ ...f, guest_email: e.target.value }))} required />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="inq-subject">Subject</Label>
                        <Input id="inq-subject" value={inquiryForm.subject} onChange={e => setInquiryForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Question about amenities" />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="inq-message">Message *</Label>
                        <Textarea id="inq-message" value={inquiryForm.message} onChange={e => setInquiryForm(f => ({ ...f, message: e.target.value }))} rows={4} required placeholder="What would you like to know?" />
                      </div>
                      <Button type="submit" disabled={inquirySubmitting} className="w-full sm:w-auto">
                        {inquirySubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        Send Message
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Right: Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-4">
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="shadow-lg border-primary/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-display text-lg flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-primary" /> Request to Book
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Submit your dates and the host will confirm availability and pricing.
                    </p>
                  </CardHeader>
                  <CardContent>
                    {bookingSuccess ? (
                      <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                          <BadgeCheck className="w-6 h-6 text-primary" />
                        </div>
                        <h3 className="font-display font-semibold text-foreground mb-1">Request Submitted!</h3>
                        <p className="text-sm text-muted-foreground">
                          The host will review your request and send you a confirmation with pricing details.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleBookingSubmit} className="space-y-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="b-name">Full Name *</Label>
                          <Input id="b-name" value={bookingForm.guest_name} onChange={e => setBookingForm(f => ({ ...f, guest_name: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="b-email">Email *</Label>
                          <Input id="b-email" type="email" value={bookingForm.guest_email} onChange={e => setBookingForm(f => ({ ...f, guest_email: e.target.value }))} required />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="b-phone">Phone</Label>
                          <Input id="b-phone" type="tel" value={bookingForm.guest_phone} onChange={e => setBookingForm(f => ({ ...f, guest_phone: e.target.value }))} />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label htmlFor="b-checkin">Check-in *</Label>
                            <Input id="b-checkin" type="date" value={bookingForm.check_in} onChange={e => setBookingForm(f => ({ ...f, check_in: e.target.value }))} required min={format(new Date(), "yyyy-MM-dd")} />
                          </div>
                          <div className="space-y-1.5">
                            <Label htmlFor="b-checkout">Check-out *</Label>
                            <Input id="b-checkout" type="date" value={bookingForm.check_out} onChange={e => setBookingForm(f => ({ ...f, check_out: e.target.value }))} required min={bookingForm.check_in || format(new Date(), "yyyy-MM-dd")} />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="b-guests">Guests</Label>
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <Input id="b-guests" type="number" min={1} max={20} value={bookingForm.guests_count} onChange={e => setBookingForm(f => ({ ...f, guests_count: parseInt(e.target.value) || 1 }))} className="w-20" />
                          </div>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="b-message">Message to host</Label>
                          <Textarea id="b-message" value={bookingForm.message} onChange={e => setBookingForm(f => ({ ...f, message: e.target.value }))} rows={3} placeholder="Tell the host about your trip..." />
                        </div>
                        <Button type="submit" className="w-full" disabled={bookingSubmitting}>
                          {bookingSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Request to Book
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </motion.div>

              {/* Trust signals */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <BadgeCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Book Direct & Save</p>
                      <p className="text-xs text-muted-foreground">No platform fees. Save 10–20% vs Airbnb.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Direct Communication</p>
                      <p className="text-xs text-muted-foreground">Chat directly with the property owner.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-50 bg-foreground/90 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button className="absolute top-4 right-4 text-background text-2xl font-bold hover:opacity-80" onClick={() => setLightboxOpen(false)}>✕</button>
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/20 flex items-center justify-center text-background hover:bg-background/40"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i - 1 + allImages.length) % allImages.length); }}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <img
            src={allImages[lightboxIndex]}
            alt={`Photo ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/20 flex items-center justify-center text-background hover:bg-background/40"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(i => (i + 1) % allImages.length); }}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute bottom-4 text-background text-sm">
            {lightboxIndex + 1} / {allImages.length}
          </div>
        </div>
      )}

      {/* Minimal Footer */}
      <footer className="border-t border-border py-6 mt-12">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground">
          Powered by <span className="font-semibold text-foreground">BnBXO</span> · Direct Booking Platform
        </div>
      </footer>
    </div>
  );
};

export default DirectBookingPage;
